import { useState } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Tooltip,
  Box,
  Text,
  useToast,
} from '@chakra-ui/react';

import UploadPolicyView from 'views/upload_policy/UploadPolicyView';
import { apiUploadFile } from 'api';
import {
  VALIDATION_TYPE_MC,
  VALIDATION_TYPE_MC_HOME,
  VALIDATION_TYPE_VALIDATE_AUTO_QUOTE,
  VALIDATION_TYPE_VALIDATE_HOME_QUOTE,
  VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY,
  VALIDATION_TYPE_COMMERCIAL_AUTO,
  MC_AUTO_SUPPORTED_PROVINCES,
  VALIDATION_TYPE_MC_LIFE,
  PROVINCE_ONTARIO,
} from 'variables/constants';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

interface PolicyUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: (
    uploadedUuid?: string,
    policyType?: ValidationType,
    isLOA?: boolean,
  ) => void;
  onLegacyAutoSelect?: () => void;
  submissionId?: number | null;
  isCreatingSubmission?: boolean;
  province?: number | null;
}

const PolicyUploadModal = ({
  isOpen,
  onClose,
  onUploadComplete,
  onLegacyAutoSelect,
  submissionId = null,
  isCreatingSubmission = false,
  province = null,
}: PolicyUploadModalProps) => {
  const toast = useToast();
  const [selectedSubmissionType, setSelectedSubmissionType] = useState<
    'personal' | 'mga' | 'commercial' | null
  >(null);
  const [selectedPolicyType, setSelectedPolicyType] =
    useState<ValidationType | null>(null);
  const [isLOA, setIsLOA] = useState<boolean | null>(null);

  // Feature flag for LOA functionality
  const isLOAEnabled = useFeatureFlag('enable-loa-specialty-auto');
  const isPersonalLOAEnabled = useFeatureFlag('enable-loa-personal-lines');
  const requiresLOASelection =
    (isLOAEnabled &&
      selectedPolicyType === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE) ||
    (isPersonalLOAEnabled &&
      (selectedPolicyType === VALIDATION_TYPE_MC ||
        selectedPolicyType === VALIDATION_TYPE_MC_HOME));
  const [isCreatingLife, setIsCreatingLife] = useState(false);
  const [isCreatingCommercial, setIsCreatingCommercial] = useState(false);

  const isLifeInsuranceEnabled = useFeatureFlag('enable-life-policy');

  const isLegacyAutoEnabled = useFeatureFlag('enable-legacy-auto-submissions');
  const isCommercialEnabled = useFeatureFlag('enable-commercial-submissions');
  const isNonOntarioAutoEnabled = useFeatureFlag(
    'enable-non-ontario-auto-submissions',
  );

  const isUnsupportedAutoProvince =
    province !== null &&
    (!MC_AUTO_SUPPORTED_PROVINCES.includes(province) ||
      (!isNonOntarioAutoEnabled && province !== PROVINCE_ONTARIO));

  const handleSubmissionTypeClick = (
    submissionType: 'personal' | 'mga' | 'commercial',
  ) => {
    setSelectedSubmissionType(submissionType);
  };

  const handlePolicyTypeSelect = (type: ValidationType) => {
    if (
      type === VALIDATION_TYPE_MC &&
      selectedSubmissionType === 'personal' &&
      isUnsupportedAutoProvince &&
      isLegacyAutoEnabled
    ) {
      onLegacyAutoSelect();
      return;
    }

    // For commercial types, handle like life - create immediately without quote
    if (
      type === VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY ||
      type === VALIDATION_TYPE_COMMERCIAL_AUTO
    ) {
      setIsLOA(false);
      handleCommercialSelection(type);
      return;
    }

    setSelectedPolicyType(type);
    // If LOA feature is disabled, skip LOA selection and set isLOA to false
    if (!isLOAEnabled && type === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE) {
      setIsLOA(false);
    }
  };

  const handleLOASelect = (loaValue: boolean) => {
    setIsLOA(loaValue);
  };

  const handleLifeSelection = async () => {
    // If submissionId is null, delegate to parent to create submission first
    if (!submissionId) {
      handleModalClose();
      onUploadComplete(undefined, VALIDATION_TYPE_MC_LIFE);
      return;
    }

    try {
      setIsCreatingLife(true);

      // Create Life policy using /upload API with mc_life type
      const formData = new FormData();
      formData.append('type', VALIDATION_TYPE_MC_LIFE);
      formData.append('submission_id', submissionId.toString());

      const response = await apiUploadFile(null, formData);
      const uuid = response.uuid;

      handleModalClose();
      onUploadComplete(uuid, VALIDATION_TYPE_MC_LIFE);
    } catch (error: any) {
      toast({
        title: 'Failed to create life policy',
        description: error?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsCreatingLife(false);
    }
  };

  const handleCommercialSelection = async (type: ValidationType) => {
    // If submissionId is null, delegate to parent to create submission first
    if (!submissionId) {
      handleModalClose();
      onUploadComplete(undefined, type);
      return;
    }

    try {
      setIsCreatingCommercial(true);

      // Create Commercial policy using /upload API with commercial type
      const formData = new FormData();
      formData.append('type', type);
      formData.append('submission_id', submissionId.toString());

      const response = await apiUploadFile(null, formData);
      const uuid = response.uuid;

      handleModalClose();
      onUploadComplete(uuid, type);
    } catch (error: any) {
      toast({
        title: 'Failed to create commercial policy',
        description: error?.error || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsCreatingCommercial(false);
    }
  };

  const handleModalClose = () => {
    setSelectedPolicyType(null);
    setSelectedSubmissionType(null);
    setIsLOA(null);
    onClose();
  };

  const handleBack = () => {
    if (requiresLOASelection && isLOA !== null) {
      setIsLOA(null);
      return;
    }
    setIsLOA(null);
    if (selectedPolicyType === VALIDATION_TYPE_VALIDATE_AUTO_QUOTE) {
      if (isLOA !== null) {
        if (isLOAEnabled) {
          setIsLOA(null);
        } else {
          setIsLOA(null);
          setSelectedPolicyType(null);
        }
      } else {
        // Go back from LOA selection to policy type
        setSelectedPolicyType(null);
      }
    } else if (selectedPolicyType) {
      setSelectedPolicyType(null);
    } else if (selectedSubmissionType) {
      setSelectedSubmissionType(null);
    }
  };

  const handleUploadComplete = (uploadedUuid?: string) => {
    onUploadComplete(
      uploadedUuid,
      selectedPolicyType || undefined,
      isLOA || false,
    );
    handleModalClose();
  };

  const getModalSize = () => {
    if (selectedPolicyType && (!requiresLOASelection || isLOA !== null)) {
      return 'xl';
    }
    return 'md';
  };

  const getModalTitle = () => {
    if (!selectedSubmissionType) {
      return 'Select Submission Type';
    }

    if (selectedSubmissionType && !selectedPolicyType) {
      return 'Select Policy Type';
    }

    if (requiresLOASelection && isLOA === null) {
      return 'Letter of Authorization (LOA)';
    }

    if (selectedPolicyType) {
      return 'Upload Policy Documents';
    }

    return 'Select Submission Type';
  };

  const shouldShowSubmissionTypes =
    !selectedSubmissionType && !selectedPolicyType;
  const shouldShowPolicyTypes = selectedSubmissionType && !selectedPolicyType;

  const shouldShowLOASelection = requiresLOASelection && isLOA === null;

  const shouldShowUpload =
    selectedPolicyType && (!requiresLOASelection || isLOA !== null);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      size={getModalSize()}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent maxW={shouldShowUpload ? '900px' : undefined}>
        <ModalHeader>
          {getModalTitle()}

          {(selectedSubmissionType || selectedPolicyType) && (
            <Button variant="ghost" size="sm" onClick={handleBack} ml={4}>
              ← Back
            </Button>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Step 1: Submission Type Selection (only for leads) */}
          {shouldShowSubmissionTypes && (
            <VStack spacing={4} align="stretch">
              <Button
                variant="brand"
                height="60px"
                fontSize="lg"
                onClick={() => handleSubmissionTypeClick('personal')}
              >
                Personal
              </Button>

              <Button
                variant="brand"
                height="60px"
                fontSize="lg"
                onClick={() => handleSubmissionTypeClick('mga')}
                isLoading={isCreatingSubmission}
              >
                PL Specialty Risks & MGA
              </Button>

              {/* Life button - show "Coming soon" when disabled */}
              {isLifeInsuranceEnabled ? (
                <Button
                  variant="brand"
                  height="60px"
                  fontSize="lg"
                  onClick={handleLifeSelection}
                  isLoading={isCreatingLife || isCreatingSubmission}
                >
                  Life
                </Button>
              ) : (
                <Tooltip
                  label="Coming soon"
                  aria-label="Coming soon"
                  placement="top"
                  hasArrow
                >
                  <Box>
                    <Button
                      variant="outline"
                      height="60px"
                      fontSize="lg"
                      isDisabled
                      width="100%"
                    >
                      Life
                    </Button>
                  </Box>
                </Tooltip>
              )}

              {/* Commercial button - show "Coming soon" when disabled */}
              {isCommercialEnabled ? (
                <Button
                  variant="brand"
                  height="60px"
                  fontSize="lg"
                  onClick={() => handleSubmissionTypeClick('commercial')}
                  isLoading={isCreatingSubmission || isCreatingCommercial}
                >
                  Commercial
                </Button>
              ) : (
                <Tooltip
                  label="Coming soon"
                  aria-label="Coming soon"
                  placement="top"
                  hasArrow
                >
                  <Box>
                    <Button
                      variant="outline"
                      height="60px"
                      fontSize="lg"
                      isDisabled
                      width="100%"
                    >
                      Commercial
                    </Button>
                  </Box>
                </Tooltip>
              )}
            </VStack>
          )}

          {/* Step 2: Policy Type Selection */}
          {shouldShowPolicyTypes && (
            <VStack spacing={4} align="stretch">
              {/* Personal Policy Types */}
              {selectedSubmissionType === 'personal' && (
                <>
                  <Button
                    variant="brand"
                    height="60px"
                    fontSize="lg"
                    onClick={() => handlePolicyTypeSelect(VALIDATION_TYPE_MC)}
                  >
                    Auto
                  </Button>
                  <Button
                    variant="brand"
                    height="60px"
                    fontSize="lg"
                    onClick={() =>
                      handlePolicyTypeSelect(VALIDATION_TYPE_MC_HOME)
                    }
                    width="100%"
                  >
                    Home
                  </Button>
                </>
              )}

              {/* MGA Policy Types */}
              {selectedSubmissionType === 'mga' && (
                <>
                  <Button
                    variant="brand"
                    height="60px"
                    fontSize="lg"
                    onClick={() =>
                      handlePolicyTypeSelect(
                        VALIDATION_TYPE_VALIDATE_AUTO_QUOTE,
                      )
                    }
                  >
                    Auto
                  </Button>
                  <Button
                    variant="brand"
                    height="60px"
                    fontSize="lg"
                    onClick={() =>
                      handlePolicyTypeSelect(
                        VALIDATION_TYPE_VALIDATE_HOME_QUOTE,
                      )
                    }
                    width="100%"
                  >
                    Home
                  </Button>
                </>
              )}

              {/* Commercial Policy Types */}
              {selectedSubmissionType === 'commercial' && (
                <>
                  <Button
                    variant="brand"
                    height="60px"
                    fontSize="lg"
                    onClick={() =>
                      handlePolicyTypeSelect(
                        VALIDATION_TYPE_COMMERCIAL_PROPERTY_CASUALTY,
                      )
                    }
                  >
                    Commercial Property & Casualty
                  </Button>
                  <Button
                    variant="brand"
                    height="60px"
                    fontSize="lg"
                    onClick={() =>
                      handlePolicyTypeSelect(VALIDATION_TYPE_COMMERCIAL_AUTO)
                    }
                    width="100%"
                  >
                    Commercial Auto
                  </Button>
                </>
              )}
            </VStack>
          )}

          {shouldShowLOASelection && (
            <VStack spacing={4} align="stretch">
              <Text fontSize="md" color="gray.600" mb={2}>
                Is this a Letter of Authorization (LOA) submission?
              </Text>
              <Button
                variant="brand"
                height="60px"
                fontSize="lg"
                onClick={() => handleLOASelect(false)}
              >
                No - Standard Submission
              </Button>
              <Button
                variant="brand"
                height="60px"
                fontSize="lg"
                onClick={() => handleLOASelect(true)}
              >
                Yes - LOA Submission
              </Button>
            </VStack>
          )}

          {/* Step 3: File Upload */}
          {shouldShowUpload && (
            <UploadPolicyView
              submissionType={selectedPolicyType}
              submissionId={submissionId}
              onSubmitUpload={handleUploadComplete}
              isCreatingSubmission={isCreatingSubmission}
              isLOA={isLOA}
              province={province}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PolicyUploadModal;
